import dayjs from "dayjs";
import { OperationalReadiness, SignOff } from "../../../api/Api";
import { getRuntimeConfig } from "../../../runtimeConfig/runtime-config";

/**
 * Operational readiness (ADHO-551): a pre-sign-off where one director each from Risk, Treasury
 * and Finance confirms the entity is actually tradable (margin call tooling, CSA review,
 * settlement risk monitoring, ...) before the VP sign-off may be sent. Temporary, until those
 * teams trust the lifecycles enough that completing one means ready to trade. Market Access can
 * mark any of the three as not needed (no signatory row), as long as one still signs.
 */
export const READINESS_DEPARTMENTS = [
  { key: "risk", label: "Risk" },
  { key: "treasury", label: "Treasury" },
  { key: "finance", label: "Finance" }
] as const;

export type ReadinessDepartment = (typeof READINESS_DEPARTMENTS)[number]["key"];

type DefaultSigner = { name: string; email: string };

// Test, dev and local propose the developer for all three, so testing never mails the real
// directors (same switch as SIGN_OFF_CEO).
const TEST_READINESS_SIGNER: DefaultSigner = {
  name: "Mathias Knudsen Laramee",
  email: "mkl@mft-energy.com"
};

// The directors Market Access normally assigns in prod (Lasse, Jakob, Danny). An empty email
// proposes nobody. Change here requires a redeploy.
const PROD_READINESS_SIGNERS: Record<ReadinessDepartment, DefaultSigner> = {
  risk: { name: "Lasse Riis Andersen", email: "lra@mft-energy.com" },
  treasury: { name: "Jakob Bering", email: "jbe@mft-energy.com" },
  finance: { name: "Danny Rasmussen Lyngbye", email: "dra@mft-energy.com" }
};

/**
 * The signer proposed on a fresh readiness. Taken from the department's assignee group when the
 * default is a member (so the display name matches the group), otherwise used as is, since the
 * test default is usually not in any of the three groups.
 */
export const defaultReadinessSigner = (
  department: ReadinessDepartment,
  groupMembers: { name?: string; email?: string }[]
): { name?: string; email?: string } | null => {
  const signer = getRuntimeConfig().PUBLIC_IS_TEST_ENV
    ? TEST_READINESS_SIGNER
    : PROD_READINESS_SIGNERS[department];
  if (!signer.email) return null;
  return (
    groupMembers.find(
      (m) => m.email?.toLowerCase() === signer.email.toLowerCase()
    ) ?? signer
  );
};

export const readinessDepartmentLabel = (department: string) =>
  READINESS_DEPARTMENTS.find((d) => d.key === department)?.label ?? department;

/**
 * Departments Market Access marked as not needing to sign, in display order. Not stored as such:
 * a not needed department simply has no signatory row, so the backend's "every listed signer
 * signed" completion is unaffected. Read back as "no row while another department has one". A
 * readiness with no rows at all is fresh, so all three are needed. Known trade-off: a draft
 * saved with a picker left blank reopens with that department as not needed (visible, untickable).
 */
export const readinessNotNeeded = (
  readiness?: OperationalReadiness | null
): ReadinessDepartment[] => {
  const signatories = readiness?.signatories ?? [];
  if (!signatories.length) return [];
  return READINESS_DEPARTMENTS.map((d) => d.key).filter(
    (key) => !signatories.some((s) => s.department === key)
  );
};

export const isReadinessInProgress = (
  readiness?: OperationalReadiness | null
) => readiness?.status === "in_progress";

export const isReadinessCompleted = (readiness?: OperationalReadiness | null) =>
  readiness?.status === "completed";

/** True while readiness is switched on but not yet signed by everyone, which blocks the VP send. */
export const isReadinessBlockingSignOff = (signOff?: SignOff | null) =>
  !!signOff?.operationalReadiness?.required &&
  !isReadinessCompleted(signOff.operationalReadiness);

/**
 * Label/value rows for the sign-off PDFs (counterparty and broker). Null when readiness was not
 * needed, so the section is left out and those PDFs read as before.
 */
export const operationalReadinessPdfRows = (
  readiness?: OperationalReadiness | null
): [string, string][] | null => {
  if (!readiness?.required) return null;
  const signatories = readiness.signatories ?? [];
  const signedCount = signatories.filter((s) => s.signed).length;
  const inProgress = isReadinessInProgress(readiness);
  const status = isReadinessCompleted(readiness)
    ? "Completed"
    : inProgress
      ? `In progress (${signedCount}/${signatories.length} signed)`
      : "Not sent";
  const rows: [string, string][] = [["Status", status]];
  if (readiness.completedAt) {
    rows.push(["Completed at", dayjs(readiness.completedAt).format("LLL")]);
  }
  signatories.forEach((s) => {
    rows.push([
      `${readinessDepartmentLabel(s.department)}: ${s.name || s.email}`,
      s.signed
        ? `Signed${s.signedAt ? ` on ${dayjs(s.signedAt).format("LLL")}` : ""}`
        : inProgress
          ? "Awaiting"
          : "Not sent"
    ]);
  });
  readinessNotNeeded(readiness).forEach((department) => {
    rows.push([readinessDepartmentLabel(department), "Not needed"]);
  });
  return rows;
};

export const isReadinessSignatoryEmail = (
  signOff: SignOff | null | undefined,
  email: string | undefined | null
): boolean =>
  !!email &&
  (signOff?.operationalReadiness?.signatories ?? []).some(
    (s) => s.email.toLowerCase() === email.toLowerCase()
  );
